import { create } from "zustand";

import { TourGuideEntity } from "./tourguide.entity";
import type {
  RegisteredTour,
  RegisteredTourStep,
  TourId,
  TourStepId,
} from "./tourguide.types";

interface TourGuideStoreState {
  activeTourId: TourId | null;
  tours: Partial<Record<TourId, RegisteredTour>>;
  targets: Record<string, HTMLElement | undefined>;
}

interface TourGuideStoreActions {
  registerTour: (
    tourId: TourId,
    steps: TourStepId[] | RegisteredTourStep[],
  ) => void;
  unregisterTour: (tourId: TourId) => void;
  registerTarget: (
    tourId: TourId,
    stepId: TourStepId,
    target: HTMLElement | null,
  ) => void;
  startTour: (tourId: TourId) => void;
  stopTour: (tourId: TourId) => void;
  nextStep: (tourId: TourId) => void;
  previousStep: (tourId: TourId) => void;
  goToStep: (tourId: TourId, stepId: TourStepId) => void;
  reset: () => void;
}

export type TourGuideStore = TourGuideStoreState & TourGuideStoreActions;

const initialState: TourGuideStoreState = {
  activeTourId: null,
  tours: {},
  targets: {},
};

function clearActiveSteps(
  tours: Partial<Record<TourId, RegisteredTour>>,
): Partial<Record<TourId, RegisteredTour>> {
  return Object.fromEntries(
    Object.entries(tours).map(([tourId, tour]): [string, RegisteredTour] => [
      tourId,
      {
        ...(tour as RegisteredTour),
        activeStepId: null,
      },
    ]),
  );
}

function normalizeSteps(
  steps: TourStepId[] | RegisteredTourStep[],
): RegisteredTourStep[] {
  return steps.map((step) => (typeof step === "string" ? { id: step } : step));
}

function stepsById(
  steps: RegisteredTourStep[],
): Record<TourStepId, RegisteredTourStep> {
  return Object.fromEntries(steps.map((step) => [step.id, step]));
}

function activateOnlyTour(
  tours: Partial<Record<TourId, RegisteredTour>>,
  tourId: TourId,
  activeStepId: TourStepId | null,
): Partial<Record<TourId, RegisteredTour>> {
  return {
    ...clearActiveSteps(tours),
    [tourId]: {
      ...tours[tourId],
      activeStepId,
    },
  };
}

export const useTourGuideStore = create<TourGuideStore>((set) => ({
  ...initialState,

  registerTour: (tourId, steps) => {
    set((state) => {
      const normalizedSteps = normalizeSteps(steps);
      const stepIds = normalizedSteps.map((step) => step.id);
      const existingTour = state.tours[tourId];
      const activeStepId =
        state.activeTourId === tourId
          ? TourGuideEntity.preservesActiveStep(
              stepIds,
              existingTour?.activeStepId ?? null,
            )
          : null;
      const activeTourId =
        state.activeTourId === tourId && activeStepId === null
          ? null
          : state.activeTourId;

      return {
        activeTourId,
        tours: {
          ...state.tours,
          [tourId]: {
            stepIds,
            activeStepId,
            steps: stepsById(normalizedSteps),
          },
        },
      };
    });
  },

  unregisterTour: (tourId) => {
    set((state) => {
      const tour = state.tours[tourId];

      if (!tour) {
        return state;
      }

      const remainingTours = { ...state.tours };
      delete remainingTours[tourId];

      return {
        activeTourId: state.activeTourId === tourId ? null : state.activeTourId,
        tours: remainingTours,
      };
    });
  },

  registerTarget: (tourId, stepId, target) => {
    set((state) => {
      const key = TourGuideEntity.targetKey(tourId, stepId);

      if (target && state.targets[key] === target) {
        return state;
      }

      if (!target && !state.targets[key]) {
        return state;
      }

      const targets = { ...state.targets };

      if (target) {
        targets[key] = target;
      } else {
        delete targets[key];
      }

      return { targets };
    });
  },

  startTour: (tourId) => {
    set((state) => {
      const tour = state.tours[tourId];

      if (!tour) {
        return state;
      }
      const activeStepId = TourGuideEntity.firstStepId(tour.stepIds);

      return {
        activeTourId: activeStepId ? tourId : null,
        tours: activateOnlyTour(state.tours, tourId, activeStepId),
      };
    });
  },

  stopTour: (tourId) => {
    set((state) => {
      const tour = state.tours[tourId];

      if (!tour || state.activeTourId !== tourId) {
        return state;
      }

      return {
        activeTourId: null,
        tours: {
          ...state.tours,
          [tourId]: {
            ...tour,
            activeStepId: null,
          },
        },
      };
    });
  },

  nextStep: (tourId) => {
    set((state) => {
      const tour = state.tours[tourId];

      if (!tour || state.activeTourId !== tourId) {
        return state;
      }
      const activeStepId = TourGuideEntity.nextStepId(
        tour.stepIds,
        tour.activeStepId,
      );

      return {
        activeTourId: activeStepId ? tourId : null,
        tours: {
          ...state.tours,
          [tourId]: {
            ...tour,
            activeStepId,
          },
        },
      };
    });
  },

  previousStep: (tourId) => {
    set((state) => {
      const tour = state.tours[tourId];

      if (!tour || state.activeTourId !== tourId) {
        return state;
      }

      return {
        activeTourId: tourId,
        tours: {
          ...state.tours,
          [tourId]: {
            ...tour,
            activeStepId: TourGuideEntity.previousStepId(
              tour.stepIds,
              tour.activeStepId,
            ),
          },
        },
      };
    });
  },

  goToStep: (tourId, stepId) => {
    set((state) => {
      const tour = state.tours[tourId];

      if (!tour || !tour.stepIds.includes(stepId)) {
        return state;
      }

      return {
        activeTourId: tourId,
        tours: activateOnlyTour(state.tours, tourId, stepId),
      };
    });
  },

  reset: () => set(initialState),
}));
